"use client";

import { Ico16Check } from "@/shared/assets/icons/ico_16_check";
import { Ico20ArrowRight } from "@/shared/assets/icons/ico_20_arrow_right";
import { Button } from "@/shared/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useBreakpoint } from "@/shared/hooks/use-breakpoint";

interface PlanCardProps {
  productType: "MPS" | "MDS";
  isYearly?: boolean;
  isLoggedIn: boolean;
}

function PlanCard({ productType, isYearly = false, isLoggedIn }: PlanCardProps) {
  const t = useTranslations();
  const router = useRouter();

  const features = [`mn_mi_mps_ye_con_1`, `mn_mi_mps_ye_con_2`, `mn_mi_mps_ye_con_3`, `mn_mi_mps_ye_con_4`];

  return (
    <div className={`pc:w-[430px] relative w-full ${isYearly ? "border-primary-500" : ""}`}>
      {isYearly && (
        <div className="rounded-60 bg-primary-500 body-sm-bold absolute top-[-10px] right-[30px] animate-bounce px-3 py-[6px] text-white">
          {t("mn_mi_mps_ye_sale")}
        </div>
      )}
      <div
        className={`tb:rounded-t-24 tb:p-7.5 rounded-t-2xl border p-6 ${isYearly ? "border-primary-500 border-[1.5px] border-b-0" : "border-gray-100"} ${productType === "MDS" ? "plan-gradient" : "bg-white"}`}
      >
        <div
          className={`tb:rounded-8 body-sm-medium tb:px-3 tb:py-[6px] tb:mb-2 mb-[6px] w-fit rounded-[6px] px-[10px] py-1 ${productType === "MDS" ? "text-primary-500 bg-white" : "bg-primary-100 text-primary-500"}`}
        >
          {productType}
        </div>
        <div className={`tb:gap-1 tb:mb-[50px] mb-8 flex flex-col gap-[2px]`}>
          <span className="heading-4-bold text-gray-900">{isYearly ? t("mn_mi_mps_ye") : t("mn_mi_mps_mo")}</span>
          <span className="body-lg-regular text-gray-700">
            {isYearly ? (
              <>
                {t("mn_mi_mps_ye_sub_1")}{" "}
                <span className="text-primary-700 body-lg-bold">{t("mn_mi_mps_ye_sub_2")}</span>{" "}
                {t("mn_mi_mps_ye_sub_3")}
              </>
            ) : (
              t("mn_mi_mds_mo_sub")
            )}
          </span>
        </div>

        <div className="tb:mb-10 relative mb-5 flex flex-col gap-[2px]">
          {isYearly && (
            <span className="body-lg-bold text-red-error absolute top-[-22px]">
              15% <span className="body-lg-regular ml-2 text-gray-300 line-through">{t("mn_mi_mds_ye_bpr_2")}</span>
            </span>
          )}
          <span className="heading-2 text-gray-900">{isYearly ? t("mn_mi_mds_ye_pri") : t("mn_mi_mds_mo_pri")}</span>
          <span className="body-lg-regular text-gray-900">
            / {isYearly ? t("mn_mi_mds_ye_pri_sub") : t("mn_mi_mds_mo_pri_sub")}
          </span>
        </div>

        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={() => {
            if (isLoggedIn) {
              router.push("/plan");
            } else {
              router.push("/login");
            }
          }}
        >
          {t("mn_mi_mds_mo_btn")} <Ico20ArrowRight className="pc:size-6 size-5" />
        </Button>
      </div>
      <div
        className={`tb:rounded-b-24 bg-secondary-100 tb:p-[30px] rounded-b-2xl border p-6 ${isYearly ? "border-primary-500 border-[1.5px] border-t-1 border-t-gray-100" : "border-t-0 border-gray-100"}`}
      >
        <div className="tb:gap-1 flex flex-col gap-[6px]">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-1">
              <Ico16Check className="tb:size-4 mt-[5px] size-[14px] shrink-0" />
              <span className="body-md-regular text-gray-500">{t(feature, { productType })}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Plan({ isLoggedIn }: { isLoggedIn: boolean }) {
  const t = useTranslations();
  const { isPc, isTablet } = useBreakpoint();

  return (
    <div className="tb:py-30 pc:px-0 tb:px-10 m-auto flex w-full flex-col items-center justify-center bg-[#ECF6FF] px-4 py-20">
      <div className="w-full max-w-[1280px]">
        <div className="tb:mb-10 mb-5 flex w-full items-center justify-between">
          <span className="heading-1 text-gray-900">{t("mn_mi_title")}</span>

          <Link href="/plan" className="body-md-regular flex items-center gap-[6px] text-gray-700">
            {t("mn_min_btn")} <Ico20ArrowRight className="tb:size-6 size-[18px]" />
          </Link>
        </div>

        {isPc ? (
          <div className="rounded-24 bg-white px-[200px] py-10">
            <Tabs defaultValue="mps" className="w-full items-center gap-0">
              <TabsList className="mb-10 h-15 gap-4 p-0">
                <TabsTrigger className="body-xl-medium h-full w-[354px] px-7 data-[state=active]:font-bold" value="mps">
                  {t("mn_mi_tab_mps")}
                </TabsTrigger>
                <TabsTrigger className="body-xl-medium h-full w-[354px] px-7 data-[state=active]:font-bold" value="mds">
                  {t("mn_mi_tab_mds")}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="mps">
                <div className="flex gap-5">
                  <PlanCard productType="MPS" isLoggedIn={isLoggedIn} />
                  <PlanCard productType="MPS" isYearly isLoggedIn={isLoggedIn} />
                </div>
              </TabsContent>
              <TabsContent value="mds">
                <div className="flex gap-5">
                  <PlanCard productType="MDS" isLoggedIn={isLoggedIn} />
                  <PlanCard productType="MDS" isYearly isLoggedIn={isLoggedIn} />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div>
            <Tabs defaultValue="mps" className="w-full items-center gap-0">
              <TabsList className="tb:mb-10 tb:gap-3 tb:h-15 mb-5 w-full gap-2 p-0">
                <TabsTrigger
                  className="body-xl-medium tb:px-7 tb:py-4 h-auto w-full px-4 py-3 data-[state=active]:font-bold"
                  value="mps"
                >
                  {isTablet ? t("mn_mi_tab_mps") : "MPS"}
                </TabsTrigger>
                <TabsTrigger
                  className="body-xl-medium tb:px-7 tb:py-4 h-auto w-full px-4 py-3 data-[state=active]:font-bold"
                  value="mds"
                >
                  {isTablet ? t("mn_mi_tab_mds") : "MDS"}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="mps" className="w-full">
                <div className="tb:flex-row tb:gap-4 flex flex-col gap-6">
                  <PlanCard productType="MPS" isLoggedIn={isLoggedIn} />
                  <PlanCard productType="MPS" isYearly isLoggedIn={isLoggedIn} />
                </div>
              </TabsContent>
              <TabsContent value="mds" className="w-full">
                <div className="tb:flex-row tb:gap-4 flex flex-col gap-6">
                  <PlanCard productType="MDS" isLoggedIn={isLoggedIn} />
                  <PlanCard productType="MDS" isYearly isLoggedIn={isLoggedIn} />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
}
